/**
 * Portal Auth — the fleet migration view.
 *
 * TEMPORARY MIGRATION TOOLING - TO BE REMOVED AFTER CUTOVER. It dies with
 * Firebase, exactly as `MigrationAuditPage.jsx` does with the MDExam cutover.
 * Its last useful act is showing every clinic teardown-ready, which is the
 * evidence that Firebase can be removed.
 *
 * One page, one question, asked of the whole fleet at once: which clinics have
 * flipped, how far each one has drained, and what still has to be true before
 * Firebase can be turned off. odin's surfaces answer per-patient and
 * per-brand questions; none of them can tell you that eleven clinics are
 * flipped, three have a broken mailer and one still has a live drip.
 *
 * Counts only — no patient PII reaches this page, not even masked, so there is
 * nothing here to redact. Drilling down to a person means leaving for odin.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { RefreshCw, Loader2, ArrowUp, ArrowDown, ArrowUpDown } from 'lucide-react'
import {
  Card, CardHeader, CardTitle, CardDescription, CardContent, Button, Badge, Input,
} from '../components/ui'
import { portalAuthAPI } from '../services/api'
import { useAuthStore } from '../stores/authStore'

/**
 * Batch size and worker count, matching `ExpressPortalsPage.jsx`'s summary
 * loader — and for a sharper reason here. Each row fans out to that clinic's
 * own MongoDB, so one request for 300 clinics is 300 database connections and
 * an answer that arrives after the operator has given up. Small batches also
 * mean the table fills progressively instead of staying blank for a minute.
 */
const ROW_BATCH_SIZE = 10
const ROW_BATCH_WORKERS = 2

const FILTERS = ['All', 'Flipped', 'Not flipped', 'Needs attention']

/** The population toggle: keys of a row's `population` and the roll-up's. */
const POPULATIONS = [
  { key: 'all', label: 'All' },
  { key: 'patients', label: 'Patients' },
  { key: 'partial_patient', label: 'Partial Patients' },
]

/** Verbatim from `portalAuthHealth.js`'s mode vocabulary. */
const MODE_LABEL = {
  core: 'Core',
  firebase: 'Firebase',
  unknown: 'Unknown',
}

const MODE_PILL = {
  core: 'success',
  firebase: 'secondary',
  unknown: 'gray',
}

/** The mailer check's states, from `metadataHealth.js`. */
const MAILER_LABEL = {
  healthy: 'OK',
  incomplete: 'Broken',
  warning: 'Warning',
  unavailable: 'Unknown',
}

const MAILER_PILL = {
  healthy: 'success',
  incomplete: 'destructive',
  warning: 'warning',
  unavailable: 'gray',
}

function formatUtc(iso) {
  if (!iso) return null
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return null
  return date.toISOString().replace('T', ' ').replace(/\.\d{3}Z$/, ' UTC')
}

function apiErrorMessage(err, fallback) {
  return err?.response?.data?.error?.message || fallback
}

/** `null` renders as an em dash, never as 0 — "not read" is not "none". */
function num(value) {
  return Number.isFinite(value) ? value.toLocaleString() : '—'
}

function percent(value) {
  return Number.isFinite(value) ? `${value}%` : '—'
}

/** Same rule as `portalAuthHealth.migratedPercent`: `null`, not 0%, for no users. */
function percentOf(part, whole) {
  return whole > 0 ? Number(((part / whole) * 100).toFixed(1)) : null
}

function emptyPopulation() {
  return Object.fromEntries(POPULATIONS.map(({ key }) => [key, { users: 0, migrated: 0, safeToDelete: 0 }]))
}

/**
 * Add one batch's roll-up into the running fleet-wide total.
 *
 * Pure arithmetic, and no policy: what counts as which mode is decided once in
 * `portalAuthHealth.js` and arrives here already decided. Every field in a roll-up is additive by contract (see
 * `summarizePortalAuthRows`), which is what makes a batched fleet summable at
 * all — so `migratedPercent` is recomputed from the summed numerator and
 * denominator rather than averaged, because averaging percentages would let ten
 * empty clinics outvote one 40k-patient clinic.
 */
function mergeRollups(total, batch) {
  if (!batch) return total
  const merged = {
    clinics: total.clinics + batch.clinics,
    byMode: {
      core: total.byMode.core + batch.byMode.core,
      firebase: total.byMode.firebase + batch.byMode.firebase,
      unknown: total.byMode.unknown + batch.byMode.unknown,
      notProvisioned: total.byMode.notProvisioned + batch.byMode.notProvisioned,
    },
    previewDrift: total.previewDrift + batch.previewDrift,
    migrated: total.migrated + batch.migrated,
    portalUsers: total.portalUsers + batch.portalUsers,
    countedClinics: total.countedClinics + batch.countedClinics,
    unknownClinics: total.unknownClinics + batch.unknownClinics,
    population: Object.fromEntries(POPULATIONS.map(({ key }) => {
      const a = total.population[key]
      const b = batch.population?.[key] || { users: 0, migrated: 0, safeToDelete: 0 }
      return [key, {
        users: a.users + b.users,
        migrated: a.migrated + b.migrated,
        safeToDelete: a.safeToDelete + b.safeToDelete,
      }]
    })),
  }
  merged.migratedPercent = merged.portalUsers > 0
    ? Number(((merged.migrated / merged.portalUsers) * 100).toFixed(1))
    : null
  return merged
}

const EMPTY_ROLLUP = {
  clinics: 0,
  byMode: { core: 0, firebase: 0, unknown: 0, notProvisioned: 0 },
  previewDrift: 0,
  migrated: 0,
  portalUsers: 0,
  migratedPercent: null,
  countedClinics: 0,
  unknownClinics: 0,
  population: emptyPopulation(),
}

function matchesFilter(row, filter) {
  switch (filter) {
    case 'Flipped':
      return row.mode?.mode !== 'firebase'
    case 'Not flipped':
      return row.mode?.mode === 'firebase'
    case 'Needs attention':
      // The morning question, as one filter: a broken mailer, a preview that
      // disagrees with production, a flipped-but-unprovisioned clinic, a
      // duplicate-email cohort that will refuse to migrate, or a row whose
      // numbers could not be read at all.
      return (
        row.mailer?.state === 'incomplete' ||
        row.mode?.drift === true ||
        (row.mode?.mode === 'core' && row.provisioned === false) ||
        (row.duplicateEmails || 0) > 0 ||
        (row.errors || []).length > 0
      )
    default:
      return true
  }
}

/**
 * Whether core still creates a Firebase account per new patient. One colour per
 * value whatever the mode: On is always amber, because every clinic still
 * creating Firebase accounts is one more thing between the fleet and teardown.
 */
function FirebaseAccountsCell({ value }) {
  if (value === 'disabled') return <Badge variant="gray">Off</Badge>
  if (value === 'enabled') return <Badge variant="warning">On</Badge>
  return <span className="text-muted-foreground">—</span>
}

/**
 * A percentage with its count and a bar. Migrated only ever rises: identities
 * are created, never deleted. A clinic off core mode has no safe-to-delete
 * figure at all, which reads "n/a" rather than 0%.
 */
function PercentCell({ pct, count, na }) {
  if (na) return <span className="text-muted-foreground">n/a</span>
  if (!Number.isFinite(pct)) {
    return (
      <div className="min-w-[7rem]">
        <span className="text-muted-foreground">{num(count)}</span>
      </div>
    )
  }
  return (
    <div className="min-w-[7rem]">
      <div className="flex items-baseline gap-2">
        <span className="font-medium tabular-nums">{pct}%</span>
        <span className="text-xs text-muted-foreground tabular-nums">{num(count)}</span>
      </div>
      <div className="mt-1 h-1.5 w-full rounded bg-muted overflow-hidden">
        <div
          className="h-full bg-green-500"
          style={{ width: `${Math.min(100, Math.max(0, pct))}%` }}
        />
      </div>
    </div>
  )
}

/** Anything not on Firebase reads as Core. */
function modeState(mode) {
  return mode === 'firebase' ? 'firebase' : 'core'
}

/**
 * Mode, with the provisioning flag folded in. Only a `false` flag shows: a
 * Firebase clinic that is not provisioned cannot be flipped yet, and a core
 * clinic that is not provisioned was flipped outside this tool. `null` (Core
 * could not be read) shows nothing here — the row's errors already say so.
 */
function ModeCell({ mode, provisioned }) {
  const state = modeState(mode?.mode)
  return (
    <div className="flex items-center gap-1.5 flex-wrap">
      <Badge variant={MODE_PILL[state] || 'gray'}>{MODE_LABEL[state] || state}</Badge>
      {mode?.drift && (
        // A clinic flipped on preview and not production looks flipped and is
        // not — which is the drift the env-catalog row was added to expose.
        <Badge
          variant="warning"
          title={`production: ${MODE_LABEL[modeState(mode.targets?.production?.mode)]} · preview: ${MODE_LABEL[modeState(mode.targets?.preview?.mode)]}`}
        >
          Preview differs
        </Badge>
      )}
      {provisioned === false && (
        state === 'core'
          ? <Badge variant="destructive">Not provisioned</Badge>
          : <span className="text-xs text-muted-foreground">not provisioned</span>
      )}
    </div>
  )
}

const MAILER_SORT_RANK = { incomplete: 0, warning: 1, healthy: 2 }
const FIREBASE_ACCOUNTS_SORT_RANK = { enabled: 0, disabled: 1 }

/**
 * Mode order: Core, then Firebase, then Firebase not provisioned. A clinic whose
 * preview disagrees with production sorts straight after its own mode.
 */
function modeSortRank(row) {
  const firebase = modeState(row.mode?.mode) === 'firebase' ? 1 : 0
  const unprovisioned = row.provisioned === false ? 1 : 0
  const drift = row.mode?.drift ? 1 : 0
  return firebase * 4 + unprovisioned * 2 + drift
}

function finite(value) {
  return Number.isFinite(value) ? value : null
}

/**
 * The sortable columns. `value` returns an array compared element by element,
 * or `null` for "nothing to sort on" (—, n/a, Unknown), which always sorts
 * last whatever the direction. `first` is the direction of the first click:
 * numbers start highest-first, everything else starts A→Z or best-first —
 * except Mailer, which starts worst-first so broken mailers surface.
 */
const COLUMNS = [
  { key: 'clinic', label: 'Clinic', first: 'asc', value: (row) => [row.clinic] },
  { key: 'mode', label: 'Mode', first: 'asc', value: (row) => (row.mode ? [modeSortRank(row)] : null) },
  {
    key: 'mailer',
    label: 'Mailer',
    first: 'asc',
    value: (row) => (row.mailer?.state in MAILER_SORT_RANK ? [MAILER_SORT_RANK[row.mailer.state]] : null),
  },
  {
    key: 'firebaseAccounts',
    label: 'Firebase accounts',
    first: 'asc',
    value: (row) => (row.firebaseAccountCreation in FIREBASE_ACCOUNTS_SORT_RANK
      ? [FIREBASE_ACCOUNTS_SORT_RANK[row.firebaseAccountCreation]]
      : null),
  },
  {
    key: 'users',
    label: 'Users',
    align: 'right',
    first: 'desc',
    value: (row, population) => {
      const users = finite(row.population?.[population]?.users)
      return users === null ? null : [users]
    },
  },
  {
    key: 'migrated',
    label: 'Migrated',
    first: 'desc',
    value: (row, population) => {
      const cell = row.population?.[population]
      const pct = finite(cell?.migratedPercent)
      return pct === null ? null : [pct, finite(cell.migrated) ?? 0]
    },
  },
  {
    key: 'safeToDelete',
    label: 'Safe to delete',
    first: 'desc',
    value: (row, population) => {
      const cell = row.population?.[population]
      const pct = finite(cell?.safeToDeletePercent)
      return pct === null ? null : [pct, finite(cell.safeToDelete) ?? 0]
    },
  },
]

function compareValues(a, b) {
  for (let index = 0; index < a.length; index += 1) {
    const x = a[index]
    const y = b[index]
    if (x === y) continue
    if (typeof x === 'string') return x.localeCompare(y)
    return x < y ? -1 : 1
  }
  return 0
}

/**
 * Sort rows by one column. Pending and valueless rows always go last; ties
 * (and the unsorted state) keep the fleet order the rows arrived in.
 */
function sortRows(rows, sort, population) {
  if (!sort) return rows
  const column = COLUMNS.find((candidate) => candidate.key === sort.key)
  if (!column) return rows
  const sign = sort.dir === 'asc' ? 1 : -1
  return rows
    .map((row, index) => ({ row, index, value: row.pending || row.batchError ? null : column.value(row, population) }))
    .sort((a, b) => {
      if (a.value === null || b.value === null) {
        if (a.value === null && b.value === null) return a.index - b.index
        return a.value === null ? 1 : -1
      }
      return sign * compareValues(a.value, b.value) || a.index - b.index
    })
    .map(({ row }) => row)
}

/** first direction → reverse → off (fleet order). */
function nextSort(current, column) {
  if (!current || current.key !== column.key) return { key: column.key, dir: column.first }
  if (current.dir === column.first) return { key: column.key, dir: column.first === 'asc' ? 'desc' : 'asc' }
  return null
}

function SortableHeader({ column, sort, onSort }) {
  const active = sort?.key === column.key
  const Icon = !active ? ArrowUpDown : sort.dir === 'asc' ? ArrowUp : ArrowDown
  const right = column.align === 'right'
  return (
    <th
      className={`p-2 font-medium ${right ? 'text-right' : 'text-left'}`}
      aria-sort={active ? (sort.dir === 'asc' ? 'ascending' : 'descending') : 'none'}
    >
      <button
        type="button"
        onClick={() => onSort(nextSort(sort, column))}
        className={`inline-flex items-center gap-1 hover:text-foreground ${right ? 'flex-row-reverse' : ''}`}
      >
        {column.label}
        <Icon className={`h-3.5 w-3.5 ${active ? '' : 'opacity-40'}`} />
      </button>
    </th>
  )
}

function Rollup({ rollup, population }) {
  const totals = rollup.population[population]
  return (
    <div className="grid gap-3 grid-cols-1 sm:grid-cols-3">
      <Card>
        <CardContent className="pt-6">
          <p className="text-xs text-muted-foreground">Migrated</p>
          <p className="text-2xl font-bold tabular-nums">{percent(percentOf(totals.migrated, totals.users))}</p>
          <p className="text-xs text-muted-foreground mt-1">
            {num(totals.migrated)} of {num(totals.users)} users
          </p>
        </CardContent>
      </Card>
      <Card>
        <CardContent className="pt-6">
          <p className="text-xs text-muted-foreground">Safe to delete</p>
          <p className="text-2xl font-bold tabular-nums">{percent(percentOf(totals.safeToDelete, totals.users))}</p>
          <p className="text-xs text-muted-foreground mt-1">
            {num(totals.safeToDelete)} of {num(totals.users)} users
          </p>
        </CardContent>
      </Card>
      <Card>
        <CardContent className="pt-6">
          <p className="text-xs text-muted-foreground">On core auth</p>
          <p className="text-2xl font-bold tabular-nums">{rollup.byMode.core}</p>
          <p className="text-xs text-muted-foreground mt-1">
            {rollup.byMode.firebase} on Firebase · {rollup.byMode.notProvisioned} not provisioned
          </p>
        </CardContent>
      </Card>
    </div>
  )
}

export default function PortalAuthPage() {
  const navigate = useNavigate()
  const { setCurrentClinic } = useAuthStore()
  const [clinics, setClinics] = useState([])
  const [rowsByClinic, setRowsByClinic] = useState({})
  const [rollup, setRollup] = useState(EMPTY_ROLLUP)
  const [refreshedAt, setRefreshedAt] = useState(null)
  const [phase, setPhase] = useState('loading')
  const [loadError, setLoadError] = useState(null)
  const [rowsLoading, setRowsLoading] = useState(false)
  const [filter, setFilter] = useState('All')
  const [population, setPopulation] = useState('all')
  const [search, setSearch] = useState('')
  const [sort, setSort] = useState(null)

  /**
   * Guards against a superseded load writing its rows in after a newer one.
   * Refresh is a full reload of every batch, and two overlapping reloads would
   * otherwise interleave their `setRowsByClinic` calls.
   */
  const loadIdRef = useRef(0)

  const loadRows = useCallback(async (names, bypassCache) => {
    const loadId = ++loadIdRef.current
    setRowsLoading(true)
    setRowsByClinic({})
    setRollup(EMPTY_ROLLUP)

    const batches = []
    for (let index = 0; index < names.length; index += ROW_BATCH_SIZE) {
      batches.push(names.slice(index, index + ROW_BATCH_SIZE))
    }

    let next = 0
    const worker = async () => {
      while (next < batches.length) {
        const batch = batches[next++]
        if (loadIdRef.current !== loadId) return
        try {
          const response = await portalAuthAPI.getRows(batch, bypassCache)
          if (loadIdRef.current !== loadId) return
          const { rows, rollup: batchRollup } = response.data.data
          setRowsByClinic((previous) => {
            const merged = { ...previous }
            for (const row of rows) merged[row.clinic] = row
            return merged
          })
          setRollup((previous) => mergeRollups(previous, batchRollup))
        } catch (err) {
          if (loadIdRef.current !== loadId) return
          // One failed BATCH, not one failed clinic — a per-clinic failure is
          // already a degraded row with its own `errors`. Each clinic in the
          // batch gets a row saying so, so the table never has silent holes.
          const message = apiErrorMessage(err, 'Unable to load portal auth rows')
          setRowsByClinic((previous) => {
            const merged = { ...previous }
            for (const clinic of batch) {
              merged[clinic] = { clinic, batchError: message }
            }
            return merged
          })
        }
      }
    }

    await Promise.all(Array.from({ length: Math.min(ROW_BATCH_WORKERS, batches.length) }, () => worker()))
    if (loadIdRef.current === loadId) setRowsLoading(false)
  }, [])

  const loadAll = useCallback(async (bypassCache) => {
    setPhase('loading')
    setLoadError(null)
    try {
      const response = await portalAuthAPI.getFleet(bypassCache)
      const names = response.data.data.clinics.map((clinic) => clinic.name)
      setClinics(response.data.data.clinics)
      setRefreshedAt(response.data.data.refreshedAt)
      setPhase('ready')
      await loadRows(names, bypassCache)
    } catch (err) {
      setLoadError(apiErrorMessage(err, 'Unable to load clinic fleet'))
      setPhase('error')
    }
  }, [loadRows])

  useEffect(() => {
    loadAll(false)
  }, [loadAll])

  /**
   * Open a clinic's drill-down.
   *
   * `ClinicDetailView` is reached by SELECTING a clinic, not by a URL segment:
   * `ExpressPortalsPage` renders the fleet list or the detail view depending on
   * `currentClinicId` in the auth store (`ExpressPortalsPage.jsx:773`). So this
   * sets the same store field the header's clinic picker sets and then
   * navigates — which is also why the header picker stays in step with what is
   * on screen. Building a `?clinic=` link instead would land on the fleet list.
   *
   * The store holds core-management's slug form of the name, so the clinic name
   * is converted with `connection.js:137`'s rule. That rule now has four copies
   * (here, `clinicResolver.js`, `portalAuthMetrics.js`, `ExpressPortalsPage.jsx`)
   * and all of them must agree.
   */
  const openClinic = useCallback((coreName) => {
    setCurrentClinic(coreName.toLowerCase().replace(/_/g, '-'))
    navigate('/admin/express-portals')
  }, [navigate, setCurrentClinic])

  const rows = useMemo(() => {
    const term = search.trim().toUpperCase()
    const visible = clinics
      .map((clinic) => rowsByClinic[clinic.name] || { clinic: clinic.name, pending: true })
      .filter((row) => (term ? row.clinic.includes(term) : true))
      .filter((row) => (row.pending ? filter === 'All' : matchesFilter(row, filter)))
    return sortRows(visible, sort, population)
  }, [clinics, rowsByClinic, filter, search, sort, population])

  if (phase === 'error') {
    return (
      <div className="space-y-6">
        <h1 className="text-2xl sm:text-3xl font-bold text-foreground">Portal Auth</h1>
        <Card>
          <CardContent className="py-6">
            <div className="text-center text-destructive space-y-3">
              <p>{loadError}</p>
              <Button variant="outline" onClick={() => loadAll(false)}>Try Again</Button>
            </div>
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-foreground">Portal Auth</h1>
        </div>
        <div className="flex items-center gap-3">
          {refreshedAt && (
            <span className="text-xs text-muted-foreground">Last refreshed: {formatUtc(refreshedAt)}</span>
          )}
          <Button variant="outline" onClick={() => loadAll(true)} disabled={phase === 'loading' || rowsLoading}>
            {rowsLoading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-2" />}
            Refresh
          </Button>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        {POPULATIONS.map(({ key, label }) => (
          <Button
            key={key}
            size="sm"
            variant={population === key ? 'default' : 'outline'}
            onClick={() => setPopulation(key)}
          >
            {label}
          </Button>
        ))}
      </div>

      <Rollup rollup={rollup} population={population} />

      <Card>
        <CardHeader>
          <CardTitle>Clinics</CardTitle>
          <CardDescription>{clinics.length} clinics</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-wrap items-center gap-2">
            {FILTERS.map((option) => (
              <Button
                key={option}
                size="sm"
                variant={filter === option ? 'default' : 'outline'}
                onClick={() => setFilter(option)}
              >
                {option}
              </Button>
            ))}
            <Input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Filter by clinic…"
              className="h-8 w-48 ml-auto"
            />
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted/50">
                <tr>
                  {COLUMNS.map((column) => (
                    <SortableHeader key={column.key} column={column} sort={sort} onSort={setSort} />
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.clinic} className="border-t align-top">
                    <td className="p-2">
                      <button
                        type="button"
                        className="font-medium hover:underline text-left"
                        onClick={() => openClinic(row.clinic)}
                        title="Open this clinic's Portal Auth tab"
                      >
                        {row.clinic}
                      </button>
                      {row.batchError && <p className="text-xs text-destructive mt-1">{row.batchError}</p>}
                      {(row.errors || []).map((error, index) => (
                        <p key={index} className="text-xs text-muted-foreground mt-1">
                          {error.message}{error.status ? ` (${error.status})` : ''}
                        </p>
                      ))}
                      {(row.duplicateEmails || 0) > 0 && (
                        // The ENSURE_EMAIL_COLLISION population: these patients
                        // cannot be given an identity at all, and today they
                        // surface one at a time as somebody who cannot sign in.
                        <p className="text-xs text-orange-600 mt-1">
                          {row.duplicateEmails} duplicate email{row.duplicateEmails === 1 ? '' : 's'}
                        </p>
                      )}
                    </td>
                    {row.pending ? (
                      <td className="p-2 text-muted-foreground" colSpan={COLUMNS.length - 1}>
                        <Loader2 className="h-3.5 w-3.5 inline animate-spin mr-1" /> reading…
                      </td>
                    ) : (
                      <>
                        <td className="p-2"><ModeCell mode={row.mode} provisioned={row.provisioned} /></td>
                        <td className="p-2">
                          <Badge variant={MAILER_PILL[row.mailer?.state] || 'gray'}>
                            {MAILER_LABEL[row.mailer?.state] || row.mailer?.state || '—'}
                          </Badge>
                        </td>
                        <td className="p-2"><FirebaseAccountsCell value={row.firebaseAccountCreation} /></td>
                        <td className="p-2 text-right tabular-nums">{num(row.population?.[population]?.users)}</td>
                        <td className="p-2">
                          <PercentCell
                            pct={row.population?.[population]?.migratedPercent}
                            count={row.population?.[population]?.migrated}
                          />
                        </td>
                        <td className="p-2">
                          <PercentCell
                            pct={row.population?.[population]?.safeToDeletePercent}
                            count={row.population?.[population]?.safeToDelete}
                            na={row.population && row.population[population].safeToDelete === null}
                          />
                        </td>
                      </>
                    )}
                  </tr>
                ))}
                {rows.length === 0 && (
                  <tr>
                    <td className="p-4 text-center text-muted-foreground" colSpan={COLUMNS.length}>
                      {phase === 'loading' ? 'Loading clinics…' : 'No clinics match this filter.'}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

        </CardContent>
      </Card>
    </div>
  )
}
